import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Row, Col, Table, Input, Button, Pagination, PaginationItem, PaginationLink, Card, CardBody, CardHeader, CardTitle, Label } from 'reactstrap';
import { useSortBy, useTable, usePagination } from 'react-table';
import { useHistory } from 'react-router';
import { useTranslate } from 'react-translate';
import LoadingLocal from 'plugins/TS-lib-utils-public/components/Loading/LoadingLocal';
import moment from "moment";

// Include a particular scss for this view
import { useDispatch, useSelector } from 'react-redux';
import { 
    actionGetEarlyStudies, 
    actionSaveCurrentKilabScroll, 
    actionSetFromDetails,
    actionSetPageIndex
} from 'store/actions/kiLabActions';
import ScrollButton from 'components/Common/ScrollButton';


const KiLabTable = () => {

    //Hook to redirect
    const history = useHistory();
    const dispatch = useDispatch();

    
    const scroll = useSelector(state => state.kiLabReducer.kilabScrollValue);
    const coming = useSelector(state => state.kiLabReducer.comingFromDetailView);
    const kiLabList = useSelector(state => state.kiLabReducer.studies);
    const pageState = useSelector(state => state.kiLabReducer.page);
    const lastPage = useSelector(state => state.kiLabReducer.lastPage);
    const pageLimit = useSelector(state => state.kiLabReducer.limit);
    const totalEntries = useSelector(state => state.kiLabReducer.totalEntries);
    const practiceId = useSelector(state => state.practicesReducer.practice._id);
    const querySearch = useSelector(state => state.kiLabReducer.querySearch);
    const user = useSelector(state => state.userReducer);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const refreshTable = useRef(false);
    const { mobile } = useSelector(state => state.commonReducer);

    //Translation library
    const t = useTranslate('kilab-list');
    const tStatusHL7 = useTranslate('hl7-status');

    //Bind the open Analysis action to a button
    const handleButtonClick = (param) => {

        if(param === undefined){

            alert("You can not open this Analysis.")
        }else{

            //Save current page scroll
            const fullPage = document.getElementById("full-page");
            dispatch(actionSaveCurrentKilabScroll(fullPage.scrollTop));

            //Redirecto to Edit view
            history.push(`/admin/kilab/editor/${param}`);
        }
    };

    const data = useMemo(
        () => 
            kiLabList && kiLabList.length ? kiLabList.map((ki,i) => {

                const reportData = {
                    id: ki.id,
                    readed: ki?.readed ? "true" : "",
                    date: moment(ki.date, "YYYYMMDDhhmmss").format(user.dateFormat),
                    changed: ki.changed,
                    doctor: ki.doctor,
                    practice: <>
                        {ki.practice}
                    </>,
                    analysis_instance: 
                    <>
                        <span>{tStatusHL7(ki.analysis)}</span>
                        <br />
                        <span>{moment(ki.updated_at, "YYYYMMDDHHmmss").format(user.dateFormat)}</span>
                    </>,
                    ai_date: ki.ai_date ? moment(ki.ai_date).format(user.dateFormat+" HH:mm:ss") : <></>,
                    //ai_date: ki.ai_date,

                    statusId : !ki.responsible || !Object.keys(ki.responsible).length ? (
                        <>
                            <span className="success-color">{t('available')}</span>
                        </>
                    ) :  typeof ki.responsible.auth_user_id !== "undefined" && ki.responsible.auth_user_id !== user.auth_user_id ? (
                        <>
                            <span className="orangeColor">{t('in-progress')}</span>
                            <br />
                            <span className="orangeColor" testId="assigned_doctor"><b>{ki.responsible.firstName} {ki.responsible.lastName}</b></span>
                        </>
                    ) : (
                        <>
                            <span className="success-color">{t('in-progress-myself')}</span>
                        </>
                    ),

                    responsible: ki.responsible,
                    action: <>
                        <Button 
                            size="md" 
                            color="info" 
                            type="button" 
                            testId={`view-study-${i}`}
                            disabled={ki.responsible && Object.keys(ki.responsible).length && ki.responsible.auth_user_id !== user.auth_user_id}
                            onClick={ () => handleButtonClick(ki._id)}
                        >
                            {t("open-analisys")}
                        </Button>
                    </>
                }

                return {...reportData};
            }) : [],
            [kiLabList, t]
    );

    const columns = useMemo(
        () => [
            {
                Header: mobile ? "ID" : "Auftragsnummer",
                accessor: "id"
            },
            {
                Header: `${t("date-of-receipt")}`,
                accessor: "date"
            },/*
            {
                Header: `${t("changed")}`,
                accessor: "changed"
            },*/
            {
                Header: `${t("practice")}`,
                accessor: "practice"
            },
            {
                Header: `${t("analysis-instance")}`,
                accessor: "analysis_instance"
            },
            {
                Header: `${t("last-ai-result-Date")}`,
                accessor: "ai_date", //"last_ai_result_date"
            },
            {
                Header: `${t("status")}`,
                accessor: "statusId"
            },
            {
                Header: `${t("action")}`,
                accessor: "action"
            },

        ], [t, mobile]
    )

    //Array for columns to be hidded on mobile devices
    const hiddenColumnsOnMobile = ["date", "changed", "statusId", "changed", "ai_date"];


    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
        columns,
        data,
        disableSortRemove: true,
        enableSortingRemoval: true,
        defaultCanSort: true,
        initialState: {
            pageSize: pageLimit,
            pageIndex: pageState,
            hiddenColumns: ["registerId", "additionalInfo"],
        },
        manualPagination: true,
        pageCount: lastPage + (totalEntries > pageLimit),
        autoResetPage: !refreshTable.current
    },
        usePagination);

    const goToPageNumber = (page) => {
        dispatch(actionSetPageIndex(page));
        gotoPage(page);
    }

    const goNextPage = () => {
        dispatch(actionSetPageIndex(pageIndex + 1));
        nextPage();
    }

    const goPreviousPage = () => {
        dispatch(actionSetPageIndex(pageIndex - 1));
        previousPage();
    }

    
    /** Execution on load component */
    useEffect(() => {
        //Reset the Scrollbar of the principal container
        document.getElementById("full-page").scrollTop = 0;
         
        //Call to recieve the studies
        dispatch(actionGetEarlyStudies({
            practiceId, 
            pageIndex, 
            pageSize,
            querySearch,
            dateFrom,
            dateTo
        }))
        /* Update on some of thoese VAR change */
    }, [practiceId, pageIndex, pageSize, querySearch]);

    useEffect(() => {
        if (kiLabList.length && scroll && coming) {
            document.getElementById("full-page").scrollTop = scroll;
            dispatch(actionSaveCurrentKilabScroll(0));
            dispatch(actionSetFromDetails(false));
        }
    }, [kiLabList]);

    //Pagination info
    const firstEntry = (pageIndex * pageLimit) + 1;
    const lastEntry = (pageIndex * pageLimit) + pageLimit > totalEntries ? totalEntries : (pageIndex * pageLimit) + pageLimit;
    const pageInfo = totalEntries > 0 ? t("pagination-info", {firstEntry, lastEntry, totalEntries}) : t("no-results");

    return (

        <div>
            <ScrollButton minScroll={600} condition={true} />
            {/*console.log(page)*/}
            {/* <Table {...getTableProps()}> */}
            <Table {...getTableProps()} className="table_labs">
            <thead>
                {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()} >
                        {headerGroup.headers.map(column => (
                            <th className={`session-table-header no-wrap ${
                                hiddenColumnsOnMobile.includes(column.id) 
                                ? "hide-on-mobile"
                                : ""}`}
                                {...column.getHeaderProps()}
                            >
                                {column.render('Header')}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody {...getTableBodyProps()}>
                {kiLabList === "loading" ? (<tr><td><LoadingLocal /></td></tr>)
                    : (
                        page.map((row, index) => {
                            prepareRow(row)
                            return (
                                <React.Fragment key={`tbody-${index}`}>
                                    <tr {...row.getRowProps((state, rowInfo) => {
                                        if (rowInfo && rowInfo.row) {
                                            return {
                                                style: {
                                                    fontWeight: !rowInfo.row.original.readed.length ? 'bold' : 'normal',
                                                }
                                            }
                                        } else return {}
                                    })}>
                                        {row.cells.map(cell => {
                                        return (
                                            <td 
                                            {...cell.getCellProps() }
                                            className={
                                                /** Conditions to hide coplumns on the smartphones */
                                                hiddenColumnsOnMobile.includes(cell.column.id) 
                                                    ? "hide-on-mobile"
                                                    : ""
                                            }
                                            >
                                                {
                                                    cell.value
                                                }
                                            </td>
                                        )
                                        })}
                                    </tr>
                                </React.Fragment>
                            )
                        })
                    )}
            </tbody>
        </Table>

        <Row className="text-center justify-content-center mt-3">
            <nav>
                
                <Pagination
                    className="pagination justify-content-end"
                    listclassname="justify-content-end"
                >
                    <PaginationItem disabled={!canPreviousPage}>
                        <PaginationLink
                            href="#previous-page"
                            onClick={e => {
                                e.preventDefault()
                                goPreviousPage()
                            }}
                            tabIndex="-1"
                            className="bg-info text-white"
                        >
                            <i className='fa fa-angle-double-left'></i>
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={pageIndex < 2}>
                        <PaginationLink href="#minus-two" onClick={e => {
                            e.preventDefault();
                            goToPageNumber(pageIndex - 2);
                        }}>
                            {canPreviousPage && pageIndex > 1 ? pageIndex - 1 : " "}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={pageIndex === 0}>
                        <PaginationLink href="#minus-one"
                            onClick={e => {
                                e.preventDefault();
                                goPreviousPage()
                            }}>
                            {!canPreviousPage ? " " : pageIndex}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem active>
                        <PaginationLink href="#actual-page" onClick={e => { e.preventDefault() }} className="bg-info">
                            {pageIndex + 1}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={!canNextPage}>
                        <PaginationLink href="#plus-one" onClick={e => {
                            e.preventDefault();
                            goNextPage();
                        }}>
                            {canNextPage ? pageIndex + 2 : " "}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={!(pageCount - pageIndex > 2)}>
                        <PaginationLink href="#plus-two" onClick={e => {
                            e.preventDefault();
                            goToPageNumber(pageIndex + 2);
                        }}>
                            {(pageCount - pageIndex > 2) ? pageIndex + 3 : " "}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={!canNextPage}>
                        <PaginationLink href="#next-page" onClick={e => {
                            e.preventDefault();
                            goNextPage();
                        }}
                            className="bg-info text-white"
                        >
                            <i className='fa fa-angle-double-right'></i>
                        </PaginationLink>
                    </PaginationItem>
                </Pagination>

                <Label>{pageInfo}</Label>
            </nav>
        </Row>
        
    </div>
    );
};

export default KiLabTable;
